import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import UserService from "Services/user.service";

import { useAppState } from "../../../../../../context/StateContext";
import { CompanySecurity } from "../../../../../../models/company.security";
import CompanySecurityService from "../../../../../../services/security.company.service";
import { checkError } from "../../../../../../util/checkError";
import { CompanySecurityKeys } from "../../../../../../util/queries";

const useActionsSecurity = (setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();
  const removeOption = () =>
    setSelectedOptions((prev: string[]) => {
      if (prev && prev?.length) {
        prev?.shift();

        return (prev || []) as string[];
      }

      return prev;
    });
  const getByCompanyId = () =>
    useQuery({
      queryKey: CompanySecurityKeys.getByCompanyId(currentUser?.companyId),
      queryFn: async () => {
        const response = await CompanySecurityService.getById(currentUser?.companyId || 0);

        if (response && response?.data) {
          return response?.data;
        }
      },
      enabled: !!currentUser?.companyId,
    });
  const updateSecurity = (id: number, callback: (companySecurity: CompanySecurity) => void) =>
    useMutation({
      mutationKey: CompanySecurityKeys.update(),
      mutationFn: (companySecurity: CompanySecurity) =>
        CompanySecurityService.update(id, companySecurity),
      onSuccess: async () => {
        removeOption();
        await queryClient.invalidateQueries({
          queryKey: CompanySecurityKeys.getByCompanyId(currentUser?.companyId),
        });
      },
      onError: (error, variables: CompanySecurity) => {
        const errorAxios = error as AxiosError<Error>;

        if (errorAxios?.response?.data?.message?.toLowerCase().includes("session")) {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
          return;
        }

        if (errorAxios?.response?.data && typeof errorAxios?.response?.data === "object") {
          checkError(errorAxios, translate("anErrorOccurredTryAgain"));
        }
        removeOption();
        const objectKeys = Object.keys(variables);

        callback(
          objectKeys.reduce(
            (prev, value) => ({ ...prev, [value]: !variables?.[value as keyof CompanySecurity] }),
            {} as CompanySecurity
          )
        );
      },
    });

  return {
    getByCompanyId,
    updateSecurity,
  };
};

export default useActionsSecurity;
